"use client";

import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { XIcon } from "lucide-react";
import * as React from "react";

import { ModalOverlayPresence } from "@/components/ui/modal-overlay-presence";
import { cn } from "@/lib/utils";

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

function SheetOverlay({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className
      )}
      {...props}
    >
      {/* The support bubble floats above every overlay: it has to be told. */}
      <ModalOverlayPresence />
      {children}
    </SheetPrimitive.Overlay>
  );
}

const sheetVariants = cva(
  "fixed z-50 gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-full w-3/4 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
      },
    },
    defaultVariants: {
      side: "right",
    },
  }
);

function SheetContent({
  side = "right",
  className,
  children,
  overlay = true,
  overlayClassName,
  hideCloseButton = false,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> &
  VariantProps<typeof sheetVariants> & {
    /** Render the dimming backdrop. Disable for non-modal sheets that should
     *  leave the page behind them visible and interactive. */
    overlay?: boolean;
    /** Extra classes for the backdrop — e.g. `backdrop-blur-sm` to frost the
     *  page behind a modal sheet. Ignored when `overlay` is false. */
    overlayClassName?: string;
    /** Hide the built-in top-right close button so the caller can supply its
     *  own dismiss affordance (e.g. a back button). */
    hideCloseButton?: boolean;
  }) {
  return (
    <SheetPortal>
      {overlay && <SheetOverlay className={overlayClassName} />}
      <SheetPrimitive.Content
        data-slot="sheet-content"
        className={cn(sheetVariants({ side }), className)}
        {...props}
      >
        {!hideCloseButton && (
          <SheetPrimitive.Close className="absolute right-4 top-4 rounded-sm touch:-m-3.5 touch:p-3.5 opacity-70 transition-opacity hover:opacity-100 focus:outline-hidden disabled:pointer-events-none data-[state=open]:bg-secondary">
            <XIcon className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        )}
        {children}
      </SheetPrimitive.Content>
    </SheetPortal>
  );
}

const SHEET_DRAG_CLOSE_THRESHOLD_PX = 80;
const SHEET_DRAG_VELOCITY_THRESHOLD = 0.5;
const SHEET_DRAG_CLOSE_MIN_MS = 140;
const SHEET_DRAG_CLOSE_MAX_MS = 260;
const SHEET_DRAG_SPRING_BACK_MS = 220;
const SHEET_DRAG_EASING = "cubic-bezier(0.32, 0.72, 0, 1)";

function findScrollableAncestor(
  start: HTMLElement,
  root: HTMLElement,
): HTMLElement | null {
  let el: HTMLElement | null = start;
  while (el && el !== root.parentElement) {
    if (el === root) return null;
    const style = window.getComputedStyle(el);
    const overflowY = style.overflowY;
    const isScrollContainer =
      (overflowY === "auto" || overflowY === "scroll") &&
      el.scrollHeight > el.clientHeight;
    if (isScrollContainer) return el;
    el = el.parentElement;
  }
  return null;
}

function releaseSheetDrag({
  sheet,
  overlay,
  sheetHeight,
  delta,
  velocity,
  onClose,
}: {
  sheet: HTMLElement;
  overlay: HTMLElement | null;
  sheetHeight: number;
  delta: number;
  velocity: number;
  onClose: () => void;
}) {
  const shouldClose =
    delta > SHEET_DRAG_CLOSE_THRESHOLD_PX ||
    (delta > 20 && velocity > SHEET_DRAG_VELOCITY_THRESHOLD);

  if (shouldClose) {
    sheet.style.transition = "";
    const remaining = Math.max(sheetHeight - delta, 1);
    const safeVelocity = Math.max(velocity, 0.6);
    const duration = Math.min(
      Math.max(remaining / safeVelocity, SHEET_DRAG_CLOSE_MIN_MS),
      SHEET_DRAG_CLOSE_MAX_MS,
    );
    sheet.style.animationDuration = `${duration}ms`;
    if (overlay) {
      overlay.style.transition = "";
      overlay.style.animationDuration = `${duration}ms`;
    }
    onClose();
    return;
  }

  sheet.style.transition = `transform ${SHEET_DRAG_SPRING_BACK_MS}ms ${SHEET_DRAG_EASING}`;
  sheet.style.transform = "";
  if (overlay) {
    overlay.style.transition = `opacity ${SHEET_DRAG_SPRING_BACK_MS}ms ${SHEET_DRAG_EASING}`;
    overlay.style.opacity = "";
  }
  const cleanup = () => {
    sheet.style.transition = "";
    if (overlay) {
      overlay.style.transition = "";
    }
    sheet.removeEventListener("transitionend", cleanup);
  };
  sheet.addEventListener("transitionend", cleanup);
}

/**
 * Returns a callback ref to attach to a wrapper inside a sheet to enable
 * iOS-style "pull-to-dismiss": vertical drags scroll the inner content until
 * it reaches the top, then continuing to drag down closes the sheet. If the
 * user scrolls the content during a gesture, drag-to-close is abandoned for
 * that gesture.
 *
 * A callback ref is used (rather than useRef + useEffect) so listeners get
 * (re)attached whenever the wrapper node mounts — Radix unmounts the sheet
 * content when closed, and a one-shot effect would miss the remount.
 */
function useScrollAwareSheetDrag({ onClose }: { readonly onClose: () => void }) {
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;
  const cleanupRef = React.useRef<(() => void) | null>(null);

  return React.useCallback((root: HTMLDivElement | null) => {
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
    if (!root) return;

    let startY: number | null = null;
    let startTime = 0;
    let scrollable: HTMLElement | null = null;
    let sheet: HTMLElement | null = null;
    let overlay: HTMLElement | null = null;
    let sheetHeight = 0;
    let dragging = false;
    let committedToScroll = false;

    const handleStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      const target = e.target as HTMLElement | null;
      const owningSheet = root.closest<HTMLElement>(
        '[data-slot="sheet-content"]',
      );
      if (!owningSheet || !target) return;

      scrollable = findScrollableAncestor(target, root);

      if (scrollable && scrollable.scrollTop > 0) {
        startY = null;
        return;
      }

      startY = e.touches[0].clientY;
      startTime = performance.now();
      sheet = owningSheet;
      sheetHeight = sheet.getBoundingClientRect().height;
      const portal = sheet.closest<HTMLElement>('[data-slot="sheet-portal"]');
      overlay =
        portal?.querySelector<HTMLElement>('[data-slot="sheet-overlay"]') ??
        null;
      dragging = false;
      committedToScroll = false;
    };

    const resetDragVisuals = () => {
      if (!sheet) return;
      sheet.style.transform = "";
      if (overlay) overlay.style.opacity = "";
    };

    const handleMove = (e: TouchEvent) => {
      if (startY === null || !sheet) return;

      if (scrollable && scrollable.scrollTop > 0) {
        committedToScroll = true;
      }
      if (committedToScroll) {
        if (dragging) {
          resetDragVisuals();
          dragging = false;
        }
        return;
      }

      const delta = e.touches[0].clientY - startY;
      if (delta <= 0) {
        if (dragging) {
          resetDragVisuals();
          dragging = false;
        }
        return;
      }

      if (!dragging) {
        if (delta < 3) return;
        dragging = true;
        sheet.style.transition = "none";
        if (overlay) overlay.style.transition = "none";
      }

      if (e.cancelable) e.preventDefault();
      sheet.style.transform = `translateY(${delta}px)`;
      if (overlay && sheetHeight > 0) {
        const progress = Math.min(delta / sheetHeight, 1);
        overlay.style.opacity = String(1 - progress * 0.6);
      }
    };

    const handleEnd = (e: TouchEvent) => {
      if (startY === null || !sheet) return;
      const wasDragging = dragging;
      const localSheet = sheet;
      const localOverlay = overlay;
      const localHeight = sheetHeight;
      const touch = e.changedTouches[0];
      const endY = touch ? touch.clientY : startY;
      const delta = endY - startY;
      const elapsed = performance.now() - startTime;
      const velocity = elapsed > 0 ? delta / elapsed : 0;

      startY = null;
      sheet = null;
      overlay = null;
      scrollable = null;
      dragging = false;
      committedToScroll = false;

      if (!wasDragging) return;

      releaseSheetDrag({
        sheet: localSheet,
        overlay: localOverlay,
        sheetHeight: localHeight,
        delta,
        velocity,
        onClose: onCloseRef.current,
      });
    };

    root.addEventListener("touchstart", handleStart, { passive: true });
    root.addEventListener("touchmove", handleMove, { passive: false });
    root.addEventListener("touchend", handleEnd);
    root.addEventListener("touchcancel", handleEnd);

    cleanupRef.current = () => {
      root.removeEventListener("touchstart", handleStart);
      root.removeEventListener("touchmove", handleMove);
      root.removeEventListener("touchend", handleEnd);
      root.removeEventListener("touchcancel", handleEnd);
    };
  }, []);
}

function SheetDragHandle({
  onClose,
  className,
  ...props
}: { readonly onClose: () => void } & Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "onPointerDown" | "onPointerMove" | "onPointerUp" | "onPointerCancel"
>) {
  const startYRef = React.useRef<number | null>(null);
  const startTimeRef = React.useRef<number>(0);
  const sheetRef = React.useRef<HTMLElement | null>(null);
  const overlayRef = React.useRef<HTMLElement | null>(null);
  const sheetHeightRef = React.useRef<number>(0);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const sheet = e.currentTarget.closest<HTMLElement>(
      '[data-slot="sheet-content"]',
    );
    if (!sheet) return;
    sheetRef.current = sheet;
    sheetHeightRef.current = sheet.getBoundingClientRect().height;
    const portal = sheet.closest<HTMLElement>('[data-slot="sheet-portal"]');
    overlayRef.current =
      portal?.querySelector<HTMLElement>('[data-slot="sheet-overlay"]') ?? null;
    startYRef.current = e.clientY;
    startTimeRef.current = performance.now();
    sheet.style.transition = "none";
    if (overlayRef.current) {
      overlayRef.current.style.transition = "none";
    }
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (startYRef.current === null || !sheetRef.current) return;
    const delta = e.clientY - startYRef.current;
    if (delta <= 0) {
      sheetRef.current.style.transform = "";
      if (overlayRef.current) {
        overlayRef.current.style.opacity = "";
      }
      return;
    }
    sheetRef.current.style.transform = `translateY(${delta}px)`;
    if (overlayRef.current && sheetHeightRef.current > 0) {
      const progress = Math.min(delta / sheetHeightRef.current, 1);
      overlayRef.current.style.opacity = String(1 - progress * 0.6);
    }
  };

  const handlePointerEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    if (startYRef.current === null || !sheetRef.current) return;
    const sheet = sheetRef.current;
    const overlay = overlayRef.current;
    const sheetHeight = sheetHeightRef.current;
    const delta = e.clientY - startYRef.current;
    const elapsed = performance.now() - startTimeRef.current;
    const velocity = elapsed > 0 ? delta / elapsed : 0;
    startYRef.current = null;
    sheetRef.current = null;
    overlayRef.current = null;

    releaseSheetDrag({
      sheet,
      overlay,
      sheetHeight,
      delta,
      velocity,
      onClose,
    });
  };

  return (
    <div
      data-slot="sheet-drag-handle"
      className={cn(
        "pt-2.5 pb-1 flex justify-center shrink-0 touch-none cursor-grab active:cursor-grabbing",
        className,
      )}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
      {...props}
    >
      <div className="h-1.5 w-10 rounded-full bg-muted-foreground/25" />
    </div>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-header"
      className={cn(
        "flex flex-col space-y-2 text-center sm:text-left",
        className
      )}
      {...props}
    />
  );
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn(
        "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
        className
      )}
      {...props}
    />
  );
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn("text-lg font-semibold text-foreground", className)}
      {...props}
    />
  );
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetDragHandle,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
  useScrollAwareSheetDrag,
};
